import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation, useParams } from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import EnterpriseMyPageHeaderContext from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import EnterpriseLoggedInMyPageHeaderQuery from 'bundles/composable-page-header-enterprise/queries/EnterpriseLoggedInMyPageHeaderQuery.graphql';
import EnterpriseLoggedOutMyPageHeaderQuery from 'bundles/composable-page-header-enterprise/queries/EnterpriseLoggedOutMyPageHeaderQuery.graphql';
import type {
  EnterpriseLoggedInMyPageHeaderQuery as EnterpriseLoggedInMyPageHeaderQueryType,
  EnterpriseLoggedInMyPageHeaderQueryVariables,
} from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedInMyPageHeaderQuery';
import type {
  EnterpriseLoggedOutMyPageHeaderQuery as EnterpriseLoggedOutMyPageHeaderQueryType,
  EnterpriseLoggedOutMyPageHeaderQueryVariables,
} from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedOutMyPageHeaderQuery';

const EnterprisePageHeaderBffProvider = ({ children }: { children: React.ReactNode }) => {
  const { programSlug, orgSlug } = useParams();
  const { query } = useLocation();

  const { id: userId } = userGet();

  // Extra logic to verify that the any type of query?.programSlug is actually a string we can use
  const isQueryAString = (inputQuery: typeof query): inputQuery is string => {
    return !!(inputQuery instanceof String);
  };
  const programSlugFromQuery = query?.programSlug;
  const orgSlugFromQuery = query?.orgSlug;
  let verifiedProgramSlugFromQuery: string | undefined;
  if (isQueryAString(programSlugFromQuery)) {
    verifiedProgramSlugFromQuery = programSlugFromQuery;
  }
  let verifiedOrgSlugFromQuery: string | undefined;
  if (isQueryAString(orgSlugFromQuery)) {
    verifiedOrgSlugFromQuery = orgSlugFromQuery;
  }
  // Same logic used on line 247 in
  // static/bundles/unified-common/providers/EnterpriseProgramDataProvider.tsx
  const programSlugWithFallback = programSlug ?? verifiedProgramSlugFromQuery;
  const orgSlugWithFallback = orgSlug ?? verifiedOrgSlugFromQuery;

  const { data: loggedInData, error: loggedInError } = useQuery<
    EnterpriseLoggedInMyPageHeaderQueryType,
    EnterpriseLoggedInMyPageHeaderQueryVariables
  >(EnterpriseLoggedInMyPageHeaderQuery, {
    variables: {
      enterpriseProgramSlug: programSlugWithFallback,
      thirdPartyOrgSlug: orgSlugWithFallback,
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
    skip: !userId,
  });

  const { data: loggedOutData, error: loggedOutError } = useQuery<
    EnterpriseLoggedOutMyPageHeaderQueryType,
    EnterpriseLoggedOutMyPageHeaderQueryVariables
  >(EnterpriseLoggedOutMyPageHeaderQuery, {
    variables: {
      enterpriseProgramSlug: programSlugWithFallback,
      thirdPartyOrgSlug: orgSlugWithFallback,
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
    skip: !!userId,
  });

  const pageData = userId ? loggedInData?.PageHeader?.queryMyPageHeader : loggedOutData?.PageHeader?.queryMyPageHeader;
  const queryError = userId ? loggedInError : loggedOutError;

  return (
    <EnterpriseMyPageHeaderContext.Provider
      value={{
        myPageHeader: pageData,
        error: queryError,
      }}
    >
      {children}
    </EnterpriseMyPageHeaderContext.Provider>
  );
};

export default EnterprisePageHeaderBffProvider;
